import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    LogOut,
    LayoutDashboard,
    Building2,
    HeartPulse,
    Users,
    Settings,
    ChevronDown,
    Loader2,
    User,
    Calendar,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { RoutesLink } from "../../utility/RouteLinks";
import { deleteCookies, permissionKeys, showToastNotification } from "@/utility/helper";
import { useDispatch, useSelector } from "react-redux";
import { AuthService } from "@/services/Auth/Auth";
import { setUserData } from "@/store/feature/UserSlice";
import type { RootState } from "@/store/store";

interface SidebarProps {
    isCollapsed: boolean;
    toggleSidebar: () => void;
    isMobileOpen: boolean;
    toggleMobileSidebar: () => void;
}

interface NavItem {
    label: string;
    tooltip?: string;
    Icon: LucideIcon;
    path?: string;
    relatedRoutes?: string[];
    roles?: string[];
    accessKey?: string;
    children?: NavItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, isMobileOpen, toggleMobileSidebar }) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const userDetails = useSelector((state: RootState) => state.user.user);
    console.log("userDetail")

    const checkAccess = (accessKey?: string) => {
        if (!accessKey) return true;

        const menuBar = userDetails?.designation?.menuBar?.keyNames;
        if (!menuBar) return false;

        if (Array.isArray(menuBar)) {
            return menuBar.some((menu: any) => menu === accessKey);
        }

        return false;
    };

    const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>(() => {
        const activeSubmenus: Record<string, boolean> = {};
        const isHrmsActive = location.pathname.startsWith(RoutesLink.superAdminDepartment) ||
            location.pathname.startsWith(RoutesLink.superAdminBranch) ||
            location.pathname.startsWith(RoutesLink.superAdminDesignation) ||
            location.pathname.startsWith(RoutesLink.shiftAndTime) ||
            location.pathname.startsWith(RoutesLink.superAdminSite) ||
            location.pathname.startsWith(RoutesLink.superAdminCampus) ||
            location.pathname.startsWith(RoutesLink.superAdminApplication) ||
            location.pathname.startsWith(RoutesLink.leaveSetting);
        const isLeavesActive = location.pathname.startsWith(RoutesLink.myLeave) ||
            location.pathname.startsWith(RoutesLink.teamLeave);

        if (isHrmsActive) activeSubmenus["HRMS Settings"] = true;
        if (isLeavesActive) activeSubmenus["Leaves"] = true;
        return activeSubmenus;
    });

    const toggleSubMenu = (label: string) => {
        setOpenSubMenus((prev) => ({
            ...prev,
            [label]: !prev[label],
        }));
    };

    const handleLogout = async () => {
        setIsLoading(true);
        try {
            const deviceId = localStorage.getItem(import.meta.env.VITE_BUSINESS + 'deviceId');
            console.log("decice id", deviceId)
            const response = await AuthService.logout(deviceId || "");
            if (response?.status) {
                showToastNotification("success", response?.message)
                deleteCookies(import.meta.env.VITE_BUSINESS + 'token');
                deleteCookies(import.meta.env.VITE_BUSINESS + 'refreshToken');
                localStorage.removeItem(import.meta.env.VITE_BUSINESS + 'token');
                localStorage.removeItem(import.meta.env.VITE_BUSINESS + 'deviceId');
                dispatch(setUserData(null))
                navigate(RoutesLink?.authRoutes?.login)
            }
        } catch (error) {
            console.log(error)
        }
        finally {
            setIsLoading(false);
        }
    }

    const sidebarItems: NavItem[] = [
        {
            Icon: LayoutDashboard,
            label: "Dashboard",
            path: RoutesLink.superAdminDashboard,
            roles: []
        },
        // attendance
        {
            Icon: Building2,
            label: "Attendance",
            roles: [],
            accessKey: permissionKeys?.attendance,
            children: [
                {
                    Icon: Building2,
                    label: "My Attendance",
                    path: RoutesLink.myAttendance,
                    relatedRoutes: [RoutesLink.attendanceLog],
                    accessKey: permissionKeys?.attendance,
                    roles: []
                },
                ...(userDetails?.designation?.canAccessTeam ? [{
                    Icon: Building2,
                    label: "Team Attendance",
                    path: RoutesLink.teamAttendance,
                    accessKey: permissionKeys?.attendance,
                    roles: []
                }] : [])
            ]
        },
        // WFH
        {
            Icon: Building2,
            label: "WFH",
            accessKey: permissionKeys?.wfh,
            roles: [],
            children: [
                {
                    Icon: Building2,
                    label: "My WFH",
                    path: RoutesLink.wfhRequest,
                    accessKey: permissionKeys?.wfh,
                    relatedRoutes: [RoutesLink.addWfh, RoutesLink.wfhDetail],
                    roles: []
                },
                ...(userDetails?.designation?.canAccessTeam ? [{
                    Icon: Building2,
                    label: "Team WFH",
                    path: RoutesLink.teamWfh,
                    accessKey: permissionKeys?.wfh,
                    relatedRoutes: [RoutesLink.teamWfhDetail],
                    roles: []
                }] : [])
            ]
        },
        // OD
        {
            Icon: Building2,
            label: "OD",
            accessKey: permissionKeys?.od,
            roles: [],
            children: [
                {
                    Icon: Building2,
                    label: "My OD",
                    path: RoutesLink.myOd,
                    accessKey: permissionKeys?.od,
                    relatedRoutes: [RoutesLink.createOd, RoutesLink.odDetail],
                    roles: []
                },
                ...(userDetails?.designation?.canAccessTeam ? [{
                    Icon: Building2,
                    label: "Team OD",
                    path: RoutesLink.teamOd,
                    accessKey: permissionKeys?.od,
                    relatedRoutes: [RoutesLink.teamOdDetail],
                    roles: []
                }] : [])
            ]
        },
        // Payslip
        {
            Icon: Building2,
            label: "Payslip",
            roles: [],
            accessKey: permissionKeys?.payslip,
            path: RoutesLink.payslip,
            relatedRoutes: [RoutesLink.payslipDetail],
        },
        // Leaves
        {
            Icon: Calendar,
            label: "Leaves",
            roles: [],
            accessKey: permissionKeys?.leave,
            children: [
                {
                    Icon: Building2,
                    label: "My Leave",
                    path: RoutesLink.myLeave,
                    accessKey: permissionKeys?.leave,
                    relatedRoutes: [RoutesLink.addLeave, RoutesLink.leaveDetail],
                    roles: []
                },
                ...(userDetails?.designation?.canAccessTeam ? [{
                    Icon: Building2,
                    label: "Team Leave",
                    path: RoutesLink.teamLeave,
                    accessKey: permissionKeys?.leave,
                    relatedRoutes: [RoutesLink.teamLeaveDetail],
                    roles: []
                },] : [])
                // {
                //     Icon: Building2,
                //     label: "Leave List",
                //     path: RoutesLink.myLeaveList,
                //     roles: []
                // }
            ]
        },
        {
            Icon: Building2,
            label: "Employees",

            path: RoutesLink.superAdminEmployees,
            roles: []
        },
        {
            Icon: Settings,
            label: "HRMS Settings",
            accessKey: permissionKeys?.hrmSetting,
            roles: [],
            children: [
                {
                    Icon: Users,
                    label: "Employees",
                    path: RoutesLink.superAdminEmplyoeeOnboard,
                    accessKey: permissionKeys?.hrmSetting,
                    relatedRoutes: [RoutesLink.addEmplyoeeOnboard, RoutesLink.editEmplyoeeOnboard, RoutesLink.employeeDetail],
                    roles: []
                },
                {
                    Icon: Building2,
                    label: "Application",
                    accessKey: permissionKeys?.hrmSetting,
                    path: RoutesLink.superAdminApplication,
                    roles: []
                },
                {
                    Icon: Building2,
                    label: "Branch",
                    accessKey: permissionKeys?.hrmSetting,
                    path: RoutesLink.superAdminBranch,
                    roles: []
                },
                {
                    Icon: Building2,
                    label: "Site",
                    accessKey: permissionKeys?.hrmSetting,
                    path: RoutesLink.superAdminSite,
                    roles: []
                },
                {
                    Icon: Building2,
                    label: "Campus",
                    accessKey: permissionKeys?.hrmSetting,
                    path: RoutesLink.superAdminCampus,
                    roles: []
                },
                {
                    Icon: Building2,
                    label: "Department",
                    accessKey: permissionKeys?.hrmSetting,
                    path: RoutesLink.superAdminDepartment,
                    roles: []
                },
                {
                    Icon: Building2,
                    label: "Designation",
                    accessKey: permissionKeys?.hrmSetting,
                    path: RoutesLink.superAdminDesignation,
                    roles: []
                },
                {
                    Icon: Building2,
                    label: "Shift and Time",
                    accessKey: permissionKeys?.hrmSetting,
                    path: RoutesLink.shiftAndTime,
                    roles: []
                },
                {
                    Icon: Building2,
                    label: "Leave Setting",
                    accessKey: permissionKeys?.hrmSetting,
                    path: RoutesLink.leaveSetting,
                    roles: []
                },
            ]
        },

    ];

    React.useEffect(() => {
        setOpenSubMenus(prev => {
            const next = { ...prev };
            let changed = false;
            sidebarItems.forEach(item => {
                if (item.children) {
                    const isActive = item.children.some(child =>
                        (child.path && location.pathname.startsWith(child.path)) ||
                        (child.relatedRoutes && child.relatedRoutes.some(r => location.pathname.startsWith(r)))
                    );
                    if (isActive && !next[item.label]) {
                        next[item.label] = true;
                        changed = true;
                    }
                }
            });
            return changed ? next : prev;
        });
    }, [location.pathname]);

    return (
        <>
            {/* Mobile Backdrop */}
            {isMobileOpen && (
                <div
                    className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm transition-opacity duration-300"
                    onClick={toggleMobileSidebar}
                />
            )}

            <aside className={`
                fixed inset-y-0 left-0 z-50 h-screen flex flex-col transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]
                bg-[#0B1121] border-r border-white/10 shadow-2xl overflow-hidden
                ${isCollapsed ? 'w-20' : 'w-72'}
                ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                {/* Subtle background glow for depth */}
                <div className="absolute top-0 left-0 w-full h-full bg-linear-to-b from-primary/5 to-transparent pointer-events-none -z-10" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[200px] bg-primary/20 blur-[100px] rounded-full pointer-events-none -z-10" />

                {/* Header */}
                <div className="p-4 border-b border-white/5 relative z-10 flex items-center justify-start">
                    <div className="flex items-center gap-3.5">
                        <div className="relative shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-primary">
                            <HeartPulse className="text-white relative z-10" size={24} strokeWidth={2.5} />
                        </div>
                        {!isCollapsed && (
                            <div className="flex flex-col">
                                <span className="font-display text-[16px] font-bold text-white tracking-wide leading-tight shadow-sm">
                                    Sadguru HRMS
                                </span>
                                <span className="text-[11px] font-medium text-primary/80 uppercase tracking-widest mt-0.5">
                                    SMT/NGO/2008
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                    <div className="space-y-1.5">
                        {sidebarItems?.map((item) => {
                            const { Icon } = item;
                            const isAccess = checkAccess(item?.accessKey);
                            if (!isAccess) return null;
                            if (item.children) {
                                const isChildActive = item.children.some(child =>
                                    (child.path && location.pathname.startsWith(child.path)) ||
                                    (child.relatedRoutes && child.relatedRoutes.some(r => location.pathname.startsWith(r)))
                                );

                                return (
                                    <div key={item.label} className="space-y-1">
                                        <button
                                            onClick={() => toggleSubMenu(item.label)}
                                            className={`
                                                w-full group relative overflow-hidden flex items-center justify-between px-4 py-3 text-[14px] font-medium rounded-xl transition-all duration-300 cursor-pointer
                                                ${isChildActive
                                                    ? 'bg-primary/10 text-white border border-primary/10'
                                                    : 'text-slate-400 border border-transparent hover:text-white hover:bg-primary/10 hover:border-primary/10'
                                                } 
                                            `}
                                        >
                                            <div className="flex items-center gap-3.5 relative z-10">
                                                <div className="flex items-center justify-center">
                                                    <Icon
                                                        size={20}
                                                        className={`transition-all duration-300 ease-out
                                                            ${isChildActive
                                                                ? 'text-primary scale-110'
                                                                : 'group-hover:text-white group-hover:scale-110'
                                                            }
                                                        `}
                                                        strokeWidth={isChildActive ? 2.5 : 2}
                                                    />
                                                </div>
                                                {!isCollapsed && (
                                                    <span className="tracking-wide transition-transform duration-300 group-hover:translate-x-1">
                                                        {item.label}
                                                    </span>
                                                )}
                                            </div>

                                            {!isCollapsed && (
                                                <ChevronDown
                                                    size={16}
                                                    className={`text-slate-400 transition-transform duration-350 relative z-10
                                                        ${openSubMenus[item.label] ? 'rotate-180 text-primary' : 'group-hover:text-white'}
                                                    `}
                                                />
                                            )}
                                        </button>

                                        {openSubMenus[item.label] && !isCollapsed && (
                                            <div className="pl-6 space-y-1 mt-1 transition-all duration-300 ease-in-out">
                                                {item.children.map((child) => {
                                                    if (!checkAccess(child?.accessKey)) return null;
                                                    const ChildIcon = child.Icon;
                                                    const isChildItemActive = (child.path && location.pathname.startsWith(child.path)) || (child.relatedRoutes && child.relatedRoutes.some(r => location.pathname.startsWith(r)));

                                                    return (
                                                        <Link
                                                            key={child.path}
                                                            to={child.path || "#"}
                                                            className={`
                                                                group relative overflow-hidden flex items-center gap-3 px-4 py-2 text-[13px] font-medium rounded-lg transition-all duration-300
                                                                ${isChildItemActive
                                                                    ? 'bg-white/5 text-white border border-white/10 shadow-[0_2px_10px_rgba(0,0,0,0.1)]'
                                                                    : 'text-slate-400 border border-transparent hover:text-white hover:bg-primary/10 hover:border-white/5'
                                                                }
                                                            `}
                                                        >
                                                            {isChildItemActive && (
                                                                <div className="absolute inset-y-0 left-0 w-1 bg-primary rounded-r" />
                                                            )}

                                                            <div className="relative z-10 flex items-center justify-center">
                                                                <ChildIcon
                                                                    size={16}
                                                                    className={`transition-all duration-300 ease-out
                                                                        ${isChildItemActive
                                                                            ? 'text-primary scale-110'
                                                                            : 'group-hover:text-white group-hover:scale-110'
                                                                        }
                                                                    `}
                                                                    strokeWidth={isChildItemActive ? 2.5 : 2}
                                                                />
                                                            </div>

                                                            <span className="tracking-wide relative z-10 transition-transform duration-300 group-hover:translate-x-1">
                                                                {child.label}
                                                            </span>
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            const isActive = (item.path && location.pathname.startsWith(item.path)) || (item.relatedRoutes && item.relatedRoutes.some(r => location.pathname.startsWith(r)));

                            return (
                                <Link
                                    key={item.path}
                                    to={item.path || "#"}
                                    className={`
                                        group relative overflow-hidden flex items-center gap-3.5 px-4 py-3 text-[14px] font-medium rounded-xl transition-all duration-300
                                        ${isActive
                                            ? 'bg-primary/10 text-white border border-primary/10'
                                            : 'text-slate-400 border border-transparent hover:bg-primary/10 hover:border-primary/10'
                                        } 
                                    `}
                                >
                                    {isActive && (
                                        <div className="absolute inset-0 bg-linear-to-r from-primary/10 to-transparent pointer-events-none" />
                                    )}

                                    <div className="relative z-10 flex items-center justify-center">
                                        <Icon
                                            size={20}
                                            className={`transition-all duration-300 ease-out
                                                ${isActive
                                                    ? 'text-primary'
                                                    : ''
                                                }
                                            `}
                                            strokeWidth={isActive ? 2.5 : 2}
                                        />
                                    </div>

                                    {!isCollapsed && (
                                        <span className="tracking-wide relative z-10 transition-transform duration-300 group-hover:translate-x-1">{item.label}</span>
                                    )}

                                    {isActive && !isCollapsed && (
                                        <div className="absolute right-4 w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                    )}
                                </Link>
                            );
                        })}
                    </div>
                </nav>

                {/* Footer Actions */}
                <div className="p-4 border-t border-white/5 bg-black/20 backdrop-blur-md relative">
                    {isProfileDropdownOpen && (
                        <>
                            <div className="fixed inset-0 z-40" onClick={() => setIsProfileDropdownOpen(false)} />
                            <div className={`absolute bottom-[calc(100%-8px)] bg-[#0B1121] border border-white/10 rounded-xl shadow-2xl z-50 p-1 flex flex-col gap-1 transition-all duration-300 ${isCollapsed ? 'left-2 right-2' : 'left-4 right-4'}`}>
                                <button
                                    onClick={() => {
                                        navigate(RoutesLink.profile);
                                        setIsProfileDropdownOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer"
                                >
                                    <User size={16} className="text-primary" />
                                    {!isCollapsed && <span>Profile</span>}
                                </button>
                                <button
                                    onClick={() => {
                                        setIsProfileDropdownOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer"
                                >
                                    <Settings size={16} className="text-primary" />
                                    {!isCollapsed && <span>Settings</span>}
                                </button>
                            </div>
                        </>
                    )}

                    {/* User Profile */}
                    <div className={`
                        relative rounded-2xl transition-all duration-300 bg-white/5 hover:bg-white/10 border border-white/10
                        ${isCollapsed ? 'p-2 flex justify-center' : 'p-3'}
                    `}>
                        <div className={`relative z-10 flex items-center gap-3 ${isCollapsed ? 'justify-center' : ''}`}>
                            <div className="relative shrink-0">
                                <span
                                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                                    className="flex h-11 w-11 text-white items-center justify-center rounded-xl border border-white/20 font-bold text-sm cursor-pointer hover:bg-white/10 hover:border-white/40 transition-all"
                                >
                                    {userDetails?.name?.charAt(0).toUpperCase()}
                                </span>
                            </div>

                            {!isCollapsed && (
                                <>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[14px] font-bold truncate tracking-wide text-white">{userDetails?.name}</div>
                                        {/* <div className="text-[11px] font-semibold text-slate-500 mt-0.5">{userDetails?.department?.name}</div> */}
                                        <div className="text-[11px] font-semibold text-slate-500 mt-0.5">{userDetails?.designation?.name}</div>
                                    </div>


                                    <button
                                        className="p-2.5 rounded-xl text-slate-400 hover:text-white hover:bg-red-500/50 transition-all duration-300 group"
                                        title="Logout"
                                        onClick={handleLogout}
                                    >
                                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                                    </button>

                                </>
                            )}
                        </div>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
